import React, { FunctionComponent, useState, SyntheticEvent } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { Link, useRouteMatch, match } from 'react-router-dom';
import withStyles from 'react-jss';
import { pathOr } from 'ramda';
import { Container } from 'semantic-ui-react';
import classnames from 'classnames';

import { ClassProp } from 'helpers/types';
import { useCategories, FoundCategory } from 'helpers/useCategories';
import { ChartPlaceholder } from 'components/Placeholder';
import Query from 'components/Query';
import FormSelect from 'components/FormSelect';
import Button from 'components/Button';
import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';
import {
  GET_BRAND_ASSETS,
  GET_ACCOUNT_DETAILS,
  APPROVE_BRAND_ASSETS,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/MerchantBrandAssets.queries';
import {
  Merchant,
  AccountDetails,
  ApproveAssetResponse,
  BrandAsset,
  DisplayLabels,
  getCategoryText,
  getApolloError,
  defaultAssetApprovalSuccessMessage,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/MerchantBrandAssets.helpers';
import Tier1Layout from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/components/Tier1Layout/Tier1Layout';
import Tier3Layout from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/components/Tier3Layout/Tier3Layout';
import styles from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/MerchantBrandAssets.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  merchant: Merchant;
  classes: Styles;
};

type QueryResponse = {
  loading: boolean;
  data: {
    brand: BrandAsset;
  };
};

const versionOptions = [
  { key: 1, text: 'Latest', value: 'LATEST' },
  { key: 2, text: 'Pending', value: 'PENDING' },
];

const bundleOptions = [
  { key: 1, text: 'Default', value: 'DEFAULT_BRANDING' },
  { key: 2, text: 'Venmo', value: 'VENMO_APP' },
];

const renderAssets = (
  accountDetails: AccountDetails,
  hasAccountDetails: boolean,
  classes: Styles,
  brandId: string,
  brandVersion: string,
  setBrandVersion: Function,
  brandBundle: string,
  setBrandBundle: Function,
  doAssetApproval: Function,
  match: match,
  isSubmitting: boolean,
  foundCategory?: FoundCategory,
  foundSubCategory?: FoundCategory,
) => (response: QueryResponse) => {
  if (response.loading) {
    return <ChartPlaceholder className={classes.loading} />;
  }

  const onChangeVersion = (
    _: SyntheticEvent<HTMLElement, Event>,
    { value }: any,
  ) => {
    setBrandVersion(value);
  };

  const onChangeBundle = (
    _: SyntheticEvent<HTMLElement, Event>,
    { value }: any,
  ) => {
    setBrandBundle(value);
  };

  const brandAssets = pathOr('', ['data', 'brand'], response);
  const updatedAt = pathOr('', ['assets', 'updatedAt'], brandAssets);
  const { BrandBundleTypes } = DisplayLabels as any;
  const categoryText = foundCategory
    ? getCategoryText(
        ['getCategoryById', 'foundCategory', 'text'],
        foundCategory,
      )
    : '';

  const subCategoryText = foundSubCategory
    ? getCategoryText(
        ['getCategoryById', 'foundCategory', 'text'],
        foundSubCategory,
      )
    : '';

  const handleClick = () => {
    doAssetApproval(updatedAt);
  };

  return (
    <Container className={classes.container}>
      <div className={classes.headerRow}>
        <div className={classes.headerGroup}>
          <label className={classes.label}>Asset Bundle:</label>
          <FormSelect
            fluid
            selectOnBlur={false}
            name="bundle"
            placeholder="Bundle"
            options={bundleOptions}
            onChange={onChangeBundle}
            value={brandBundle}
            className={classes.formSelect}
          />
        </div>

        <div className={classes.headerGroup}>
          <label className={classes.label}>Asset Version:</label>
          <FormSelect
            fluid
            selectOnBlur={false}
            name="version"
            placeholder="Version"
            options={versionOptions}
            onChange={onChangeVersion}
            value={brandVersion}
            className={classes.formSelect}
          />
        </div>
      </div>
      <div className={classes.headerRow}>
        <div className={classes.headerGroup}>
          <label className={classes.label}>Brand Id:</label>
          {brandId}
        </div>

        <div className={classes.headerGroup}>
          <label className={classes.label}>Last Updated:</label>
          {updatedAt}
        </div>
      </div>

      {hasAccountDetails && (
        <Tier3Layout
          accountDetails={accountDetails}
          brandAssets={brandAssets}
          category={categoryText}
          subCategory={subCategoryText}
        />
      )}
      {!hasAccountDetails && (
        <Tier1Layout brandAssets={brandAssets} brandBundle={brandBundle} />
      )}
      {brandVersion === 'PENDING' && (
        <Button
          onClick={handleClick}
          title={isSubmitting ? '...Submitting' : 'Approve Assets'}
        />
      )}
      {brandVersion === 'LATEST' && (
        <Link
          className={classnames(classes.button, classes.mediumBtn)}
          to={`${match.url}/asset-management?brandBundle=${brandBundle}`}
        >
          {`Update ${BrandBundleTypes[brandBundle]}`}
        </Link>
      )}
    </Container>
  );
};

const BrandAssets: FunctionComponent<Props> = ({ classes, merchant }) => {
  const [accountDetails, setAccountDetails] = useState<AccountDetails | {}>({});
  const [brandVersion, setBrandVersion] = useState<string>('LATEST');
  const [brandBundle, setBrandBundle] = useState<string>('DEFAULT_BRANDING');
  const match = useRouteMatch();

  const { merchantId, brandId, hasAccountDetails } = merchant;

  const {
    getCategoryById,
    getSubCategoryById,
    foundCategory,
    foundSubCategory,
  } = useCategories();

  const handleSuccess = (success: any) =>
    toastMapping({}, 'success')(success as any);

  const handleAssetApprovalResponse = (
    approveAssetResponse: ApproveAssetResponse,
  ) => {
    const approvalSuccess = pathOr(
      false,
      ['approveBrandAsset', 'success'],
      approveAssetResponse,
    );

    if (approvalSuccess) {
      setBrandVersion('LATEST');
      return handleSuccess(defaultAssetApprovalSuccessMessage);
    }
  };

  const doAssetApproval = (updatedAt: string) =>
    approveAssets({
      variables: {
        updatedAt,
        brandId,
        bundle: brandBundle,
      },
    });

  const handleError = (error: any) => {
    toastMapping({}, 'error')(getApolloError(error) as any);
  };

  const [approveAssets, { loading: isSubmitting }] = useMutation(
    APPROVE_BRAND_ASSETS,
    {
      onCompleted: handleAssetApprovalResponse,
      onError: handleError,
      refetchQueries: [
        {
          query: GET_BRAND_ASSETS,
          variables: {
            id: brandId || merchantId.replace(/-/g, ''),
            version: 'LATEST',
            bundle: brandBundle,
          },
        },
      ],
    },
  );

  const doCompleted = (data: any): void => {
    const accountDetails = pathOr({}, ['getAccountDetails'], data);
    const { category, subCategory } = accountDetails;

    setAccountDetails(accountDetails);
    getCategoryById({
      variables: {
        params: {
          id: category,
        },
      },
    });
    if (subCategory) {
      getSubCategoryById({
        variables: {
          params: {
            id: subCategory,
          },
        },
      });
    }
  };

  const { loading } = useQuery(GET_ACCOUNT_DETAILS, {
    onCompleted: doCompleted,
    skip: !hasAccountDetails,
    variables: {
      params: {
        merchantUuid: merchantId,
      },
    },
  });

  if (loading) return null;

  return (
    <Query
      enableProgressInfo
      query={GET_BRAND_ASSETS}
      fetchPolicy={'network-only'}
      render={renderAssets(
        accountDetails as AccountDetails,
        hasAccountDetails,
        classes,
        brandId,
        brandVersion,
        setBrandVersion,
        brandBundle,
        setBrandBundle,
        doAssetApproval,
        match,
        isSubmitting,
        foundCategory,
        foundSubCategory,
      )}
      variables={{
        id: brandId || merchantId.replace(/-/g, ''),
        version: brandVersion,
        bundle: brandBundle,
      }}
    />
  );
};

export default withStyles(styles)(BrandAssets);
